import { alpha, type Components, type Theme } from '@mui/material/styles';
import { controlHeight, touchTarget } from '@munifocus/tokens';
import { type makePalette } from './palette';
import { makeFocusVisibleBaseline } from './focus';

/**
 * The MUI component overrides, built against one portal's palette.
 *
 * A function rather than a constant because the primary button fill and the app
 * bar are the two places the portal accent is most visible, and baking the
 * default palette in here would have left every portal with a teal button on a
 * teal header regardless of its own family.
 */
export function makeComponents(palette: ReturnType<typeof makePalette>): Components<Theme> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        ...makeFocusVisibleBaseline(palette),
        // Paint the root element too — otherwise the macOS rubber-band overscroll
        // reveals the unstyled (white) `html` behind the page. `overscroll-behavior`
        // also stops the bounce from chaining past the document.
        // The page's own scrollbar sits on the cream rest colour rather than a
        // white stripe, so the one vertical bar the layout has reads as part of
        // the page rather than a foreign gutter.
        html: {
          backgroundColor: palette.background.default,
          overscrollBehavior: 'none',
          scrollbarColor: `${alpha(palette.text.primary, 0.28)} ${palette.background.default}`,
        },
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          overscrollBehavior: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        // The size names carry the height, so no form has to nudge `py` until
        // its submit button looks close to the fields above it. `large` is
        // deliberately the same height as an outlined input: a submit button
        // that stands with the field it follows is the whole point of the step.
        sizeSmall: { minHeight: controlHeight.sm },
        sizeMedium: { minHeight: controlHeight.md },
        sizeLarge: { minHeight: controlHeight.lg },
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
          transition:
            'background-color 200ms ease-out, border-color 200ms ease-out, color 200ms ease-out',
          '&:active': { transform: 'scale(0.98)' },
          '&.MuiButton-containedPrimary': {
            backgroundColor: palette.brand.accent,
            // Hover used to lighten to `brand.tealLight`, which put white text at
            // 2.4:1 — the hover state failed 1.4.3 even when the rest state
            // passed. Darkening instead keeps the label at 6.9:1 throughout.
            '&:hover': { backgroundColor: palette.brand.accentDark },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // Pinned to the same scale as the buttons rather than left to MUI's
        // default, so the pair cannot drift apart in a future MUI bump.
        root: {
          minHeight: controlHeight.lg,
          // WCAG 1.4.11: the outline is the only thing identifying a text field,
          // so it needs 3:1 against the surface. The decorative hairline tokens
          // do not reach that; `border.interactive` does.
          '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.border.interactive },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: palette.background.paper,
          border: `1px solid ${palette.border.onWhite}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 4, fontWeight: 600, letterSpacing: '0.04em' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: palette.brand.rail,
          color: palette.text.onDark,
          borderBottom: `1px solid ${palette.brand.rail}`,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: palette.border.onCream },
      },
    },
    MuiMenu: {
      // A menu closes instantly. While a Popover plays its exit transition its
      // Modal still covers the viewport with an invisible backdrop, so the
      // first click after picking an option — Save, in the New promo code
      // dialog — landed on that backdrop and did nothing; only a second click
      // got through. Opening keeps the 200ms motion of everything else.
      defaultProps: { transitionDuration: { enter: 200, exit: 0 } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // An officer on a phone at a site visit taps a 28px bin icon with a
          // thumb. On a coarse pointer an invisible pseudo-element grows the
          // hit area to the 44px floor around the button's centre, so the
          // icon, its padding and the desktop layout are exactly as drawn —
          // only the part that answers a finger gets bigger. (ButtonBase is
          // already `position: relative`, which anchors it.)
          '@media (pointer: coarse)': {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: touchTarget,
              height: touchTarget,
              minWidth: '100%',
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
            },
          },
        },
      },
    },
  };
}
